import { useState } from 'react';
import axios from 'axios';
import { Send, Upload, X, FileText, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// const WA_SERVICE_URL = 'http://localhost:3001';
const WA_SERVICE_URL = process.env.REACT_APP_WA_SERVICE_URL;

const SendMessage = () => {
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      
      // Create preview for images
      if (selectedFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(selectedFile);
      } else {
        setPreview(null);
      }
    }
  };

  const removeFile = () => {
    setFile(null);
    setPreview(null);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!phone) {
      toast.error('Nomor telepon harus diisi');
      return;
    }

    if (!message && !file) {
      toast.error('Pesan atau file harus diisi');
      return;
    }

    setSending(true);

    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      if (file) {
        // Send with media
        const formData = new FormData();
        formData.append('userId', user.id);
        formData.append('phone', phone);
        formData.append('caption', message);
        formData.append('file', file);

        await axios.post(`${WA_SERVICE_URL}/send-media`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success('Pesan dengan media berhasil dikirim!');
      } else {
        // Send text only
        await axios.post(`${API}/whatsapp/send-message`, {
          phone,
          message
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Pesan berhasil dikirim!');
      }

      // Reset form
      setPhone('');
      setMessage('');
      setFile(null);
      setPreview(null);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(error.response?.data?.error || 'Gagal mengirim pesan');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-4xl md:text-5xl font-bold text-gray-800">Kirim Pesan</h1>
        <p className="text-gray-600 mt-2">Kirim pesan teks atau media ke nomor WhatsApp</p>
      </div>

      {/* Form Card */}
      <Card className="glass-effect">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="text-green-600" size={24} />
            Form Pengiriman
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSendMessage} className="space-y-6">
            {/* Phone Number */}
            <div className="space-y-2">
              <Label htmlFor="phone">Nomor WhatsApp</Label>
              <Input
                id="phone"
                data-testid="phone-input"
                type="text"
                placeholder="628123456789"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="border-gray-300 focus:border-green-500"
              />
              <p className="text-sm text-gray-500">Format: 628xxx (tanpa +)</p>
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="message">Pesan</Label>
              <Textarea
                id="message"
                data-testid="message-input"
                placeholder="Tulis pesan Anda di sini..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                className="border-gray-300 focus:border-green-500 resize-none"
              />
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label>Media (Opsional)</Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-green-500 transition-colors">
                {!file ? (
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <Upload className="mx-auto text-gray-400 mb-2" size={40} />
                    <p className="text-gray-600 mb-1">Klik untuk upload file</p>
                    <p className="text-sm text-gray-500">Gambar, dokumen, video, atau audio</p>
                    <input
                      id="file-upload"
                      data-testid="file-input"
                      type="file"
                      className="hidden"
                      onChange={handleFileChange}
                      accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx"
                    />
                  </label>
                ) : (
                  <div className="space-y-4">
                    {preview ? (
                      <img src={preview} alt="Preview" className="max-h-48 mx-auto rounded-lg" />
                    ) : (
                      <div className="flex items-center justify-center">
                        {file.type.startsWith('image/') ? (
                          <ImageIcon className="text-gray-400" size={48} />
                        ) : (
                          <FileText className="text-gray-400" size={48} />
                        )}
                      </div>
                    )}
                    <div className="flex items-center justify-center gap-4">
                      <p className="text-gray-700 font-medium">{file.name}</p>
                      <Button
                        type="button"
                        onClick={removeFile}
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        data-testid="remove-file-button"
                      >
                        <X size={16} className="mr-1" />
                        Hapus
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              data-testid="send-button"
              disabled={sending}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-6 text-lg"
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Mengirim...
                </>
              ) : (
                <>
                  <Send className="mr-2" size={20} />
                  Kirim Pesan
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SendMessage;
